import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Award, Heart, Shield, ShieldCheck, Sparkles, TrendingUp, X } from "lucide-react";
import { streakInfo, buyShield, reviveStreak } from "../lib/api";

export default function StreakConsole({ onChange, heat = [], uptimePct }) {
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(null); // { kind: 'shield' | 'revive', cost, label }

    const load = useCallback(async () => {
        try {
            const { data } = await streakInfo();
            setInfo(data);
        } catch (err) {
            console.warn("[streak] info fetch failed:", err?.message || err);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const askShield = () => setConfirm({
        kind: "shield",
        cost: info.shield_cost,
        title: "Arm Streak Shield?",
        body: `If you miss a single day, this shield will be automatically consumed to keep your ${info.streak_count}-day streak alive. Costs 3× your daily goal (${info.quota?.required_coins} coins/day right now).`,
    });
    const askRevive = () => setConfirm({
        kind: "revive",
        cost: info.revive_cost,
        title: `Revive ${info.lost_streak}-day streak?`,
        body: `This restores your previous streak of ${info.lost_streak} days. You have ${info.revive_days_left ?? "?"} day${info.revive_days_left === 1 ? "" : "s"} left — the revive window stays open for ${info.revive_window_days || 7} days after a break.`,
    });

    const runConfirmed = async () => {
        const action = confirm.kind;
        setBusy(true);
        try {
            if (action === "shield") {
                await buyShield();
                toast.success("Streak Shield armed.");
            } else {
                const { data } = await reviveStreak();
                toast.success(`Streak restored to ${data.restored_to} days.`);
            }
            setConfirm(null);
            await load();
            onChange && onChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Action failed");
        } finally {
            setBusy(false);
        }
    };

    if (!info) return null;

    const { streak_count, tier, next_tier, multiplier, quota, next_quota, quota_ramping, progress, met_today, shield_active, shield_cost, can_revive, lost_streak, revive_cost, revive_days_left, mode, routine } = info;
    const isRoutine = mode === "routine" && routine?.items?.length > 0;
    const coinPct = quota.required_coins > 0 ? Math.min(100, (progress.coins_total / quota.required_coins) * 100) : 100;
    const exPct = quota.required_exercises > 0 ? Math.min(100, (progress.exercises_total / quota.required_exercises) * 100) : 100;
    const uptime = uptimePct || Math.min(99.9, 92 + (streak_count || 0) * 1.23).toFixed(1);

    return (
        <div className="panel uptime-panel" data-testid="streak-console">
            <div className="up-top">
                <div className="eyebrow">Streak monitor</div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                    {shield_active ? (
                        <span className="badge cyan" data-testid="shield-active-badge">
                            <ShieldCheck size={14} /> Shield armed
                        </span>
                    ) : streak_count >= 3 ? (
                        <button onClick={askShield} disabled={busy} className="failover disabled:opacity-40" data-testid="buy-shield-button" title="Spend coins to protect your streak for one missed day">
                            <Shield size={14} /> Shield <span style={{ color: "var(--gold)" }}>- {shield_cost}</span>
                        </button>
                    ) : null}
                    {can_revive && (
                        <button onClick={askRevive} disabled={busy} className="btn btn-primary btn-sm disabled:opacity-40" data-testid="revive-button" title={revive_days_left != null ? `${revive_days_left} day${revive_days_left === 1 ? "" : "s"} left to revive` : undefined}>
                            <Heart size={14} /> Revive {lost_streak}d - {revive_cost}
                        </button>
                    )}
                </div>
            </div>

            <div className="up-big">
                <div className="d" data-testid="streak-count">{streak_count}<small>d</small></div>
                <div>
                    <div className="up-pct">{uptime}% UPTIME</div>
                    <span className="badge gold" data-testid="streak-tier">
                        <Award size={13} /> {tier.name} - {multiplier.toFixed(2)}x coins
                    </span>
                </div>
            </div>
            {next_tier && (
                <div className="up-next">
                    {next_tier.days_to_go} day{next_tier.days_to_go === 1 ? "" : "s"} to <b>{next_tier.name.toUpperCase()}</b>
                    {" - unlocks "}{next_tier.multiplier}x coins
                </div>
            )}

            {heat.length > 0 && (
                <div className="mt-[22px]">
                    <div className="sprint-row !m-0">
                        <span className="k">30-day uptime</span>
                        <span className="v" style={{ color: "var(--green)" }}>{heat.filter((d) => d === 1).length} green - {heat.filter((d) => d === 2).length} degraded - {heat.filter((d) => d === 0).length} down</span>
                    </div>
                    <div className="heat">
                        {heat.map((v, i) => (
                            <i key={i} className={v === 1 ? "ok" : v === 2 ? "warn" : v === 0 ? "down" : "future"} title={`day ${i + 1}`} />
                        ))}
                    </div>
                    <div className="heat-legend">
                        <span><b style={{ background: "var(--green)" }} />trained</span>
                        <span><b style={{ background: "var(--amber)" }} />partial</span>
                        <span><b style={{ background: "var(--red)" }} />missed</span>
                    </div>
                </div>
            )}

            <div className="sprint">
                <div className="sprint-row !mt-0">
                    <span className="eyebrow" style={{ color: "var(--tx-2)" }}>Today's goal</span>
                    <span className={`badge ${met_today ? "green" : "amber"}`} data-testid="streak-status">
                        {met_today ? <><Sparkles size={11} /> Done</> : "In progress"}
                    </span>
                </div>
                {isRoutine ? (
                    routine.items.map((it) => (
                        <QuotaBar
                            key={it.exercise_id}
                            label={it.name}
                            current={it.done}
                            target={`${it.target}${it.unit === "seconds" ? "s" : ""}`}
                            pct={Math.min(100, (it.done / it.target) * 100)}
                            testid={`quota-routine-${it.slug}`}
                        />
                    ))
                ) : (
                    <>
                        <QuotaBar label="Coins" current={progress.coins_total} target={quota.required_coins} pct={coinPct} testid="quota-coins" />
                        <QuotaBar label="Workouts" current={progress.exercises_total} target={quota.required_exercises} pct={exPct} testid="quota-exercises" />
                        {next_quota && (
                            <div className="quota-next" data-testid="quota-next-preview">
                                <div className="qn-top">
                                    <span className="qn-k"><TrendingUp size={11} /> Daily goal</span>
                                    <span className="qn-v">{quota.required_coins} coins / day</span>
                                </div>
                                <div className="qn-sub">
                                    Rises to <b>{next_quota.required_coins} coins/day</b> at streak day {next_quota.at_streak_day}
                                    {next_quota.days_to_go > 0 && <> — in {next_quota.days_to_go} day{next_quota.days_to_go === 1 ? "" : "s"}</>}
                                </div>
                                {quota_ramping && (
                                    <div className="qn-ramp" data-testid="quota-ramp-note">
                                        Economy update ramping in — your goal grows at most +20 coins/day until it settles.
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
                {!met_today && (
                    <div className="up-next !mt-3">
                        {isRoutine
                            ? "Your routine is the contract — complete every move to count this day toward your streak."
                            : "Finish today's goal to count this day toward your streak."}
                    </div>
                )}
            </div>

            {confirm && (
                <div className="fixed inset-0 z-[120] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid="confirm-modal">
                    <div className="panel max-w-sm w-full max-h-[85vh] overflow-y-auto">
                        <div className="flex items-start justify-between">
                            <div className="eyebrow" style={{ color: "var(--gold)" }}>Confirm purchase</div>
                            <button onClick={() => setConfirm(null)} disabled={busy} className="text-zinc-500 hover:text-white" data-testid="confirm-close">
                                <X size={18} />
                            </button>
                        </div>
                        <h3 className="heading text-2xl uppercase font-bold mt-2 text-white" data-testid="confirm-title">{confirm.title}</h3>
                        <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{confirm.body}</p>
                        <div className="card mt-5 flex items-center justify-between" data-testid="confirm-cost">
                            <div className="eyebrow">Cost</div>
                            <div className="metric text-2xl text-[var(--gold)]">-{confirm.cost} <span className="text-sm text-zinc-500">coins</span></div>
                        </div>
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setConfirm(null)} disabled={busy} className="btn btn-ghost flex-1 disabled:opacity-40" data-testid="confirm-cancel">
                                CANCEL
                            </button>
                            <button onClick={runConfirmed} disabled={busy} className="btn btn-primary flex-1 disabled:opacity-40" data-testid="confirm-accept">
                                {busy ? "WORKING..." : "CONFIRM"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function QuotaBar({ label, current, target, pct, testid }) {
    return (
        <div data-testid={testid}>
            <div className="sprint-row">
                <span className="k">{label}</span>
                <span className="v">{Math.round(current * 10) / 10} / {target}</span>
            </div>
            <div className={`bar ${label === "Coins" ? "gold" : ""}`}>
                <span style={{ width: `${pct}%`, background: pct >= 100 ? "var(--green)" : undefined }} />
            </div>
        </div>
    );
}
